import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusPill } from "@/components/common";
import { Plus, PencilSimple, Trash, Star } from "@phosphor-icons/react";

/**
 * Phase 1 admin pools table for one server kind (opensips|media|ai_engine|pd|webrtc).
 * Pool names are clickable when ``onOpenPool`` is provided (drill into that pool's servers).
 */
export default function ServerPoolsPanel({
  kind,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  onPoolsChange,
  onOpenPool,
  testIdPrefix = "pool",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/servers/pools", { params: { kind } });
      setRows(Array.isArray(data) ? data : []);
      onPoolsChange?.(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [kind, onPoolsChange]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setName(""); setOpen(true); };
  const openEdit = (p) => { setEdit(p); setName(p.name || ""); setOpen(true); };

  const save = async () => {
    const n = name.trim();
    if (!n) { toast.error("Pool name is required"); return; }
    setSaving(true);
    try {
      if (edit) {
        await api.patch(`/servers/pools/${edit.id}`, { name: n });
        toast.success("Pool updated");
      } else {
        await api.post("/servers/pools", { name: n, kind, status: "active" });
        toast.success("Pool created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/servers/pools/${p.id}/default`, { is_default: !p.is_default });
      toast.success(p.is_default ? "Default cleared" : "Set as default pool");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const del = async () => {
    const p = confirmDelete;
    if (!p) return;
    setConfirmDelete(null);
    setBusy(p.id);
    try {
      await api.delete(`/servers/pools/${p.id}`);
      toast.success("Pool deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mb-6" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Pools</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Servers belong to a pool. Delete a pool only when it has no servers.
          </p>
        </div>
        {canAdd && (
          <Button onClick={openNew} size="sm" className="rounded-lg" data-testid={`${testIdPrefix}-add-btn`}>
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Pool
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading pools…</div>
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pool Name</TableHead>
                <TableHead className="text-right">Total servers</TableHead>
                <TableHead className="text-right">Assigned Customers</TableHead>
                <TableHead className="text-right">Assigned Resellers</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-sm text-muted-foreground text-center py-6">
                    No pools yet — a Default pool is created automatically when you open this page.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`${testIdPrefix}-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {onOpenPool ? (
                      <button
                        type="button"
                        onClick={() => onOpenPool(p)}
                        className="text-left text-foreground hover:text-primary hover:underline underline-offset-2"
                        data-testid={`${testIdPrefix}-open-${p.id}`}
                      >
                        {p.name}
                      </button>
                    ) : (
                      p.name
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{p.total_servers ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.assigned_customers ?? 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.assigned_resellers ?? 0}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => setDefault(p)}
                        disabled={busy === p.id}
                        className={`inline-flex items-center gap-1 text-xs ${p.is_default ? "text-amber-500" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`${testIdPrefix}-default-${p.id}`}
                      >
                        <Star className="w-4 h-4" weight={p.is_default ? "fill" : "regular"} />
                        {p.is_default ? "Default" : "Set"}
                      </button>
                    ) : (
                      p.is_default ? <span className="text-xs text-amber-500">Default</span> : <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell><StatusPill status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      {canEdit && (
                        <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0"
                          onClick={() => openEdit(p)} data-testid={`${testIdPrefix}-edit-${p.id}`}
                          title="Edit" aria-label="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => setConfirmDelete(p)}
                          disabled={(p.total_servers || 0) > 0}
                          title={(p.total_servers || 0) > 0 ? "Move servers out of this pool first" : "Delete"}
                          aria-label="Delete"
                          data-testid={`${testIdPrefix}-delete-${p.id}`}>
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testIdPrefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>{edit ? "Edit pool" : "Add pool"}</DialogTitle>
            <DialogDescription>Pool name for this server type.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Pool name</Label>
            <Input
              data-testid={`${testIdPrefix}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Production US-East"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving || !name.trim()} className="rounded-lg" data-testid={`${testIdPrefix}-submit`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid={`${testIdPrefix}-confirm-delete`}>
          <DialogHeader>
            <DialogTitle>Delete pool?</DialogTitle>
            <DialogDescription>
              Delete “{confirmDelete?.name}”? Only allowed when the pool has no servers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={del}
              data-testid={`${testIdPrefix}-confirm-ok`}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
